import { Controller } from "@hotwired/stimulus";
import { get } from "@rails/request.js";

// Detects when a turbo-cable-stream-source reconnects after a disconnection
// (e.g. during deploys or dev code reloading) and fetches any messages that
// were missed during the gap, without replacing the page or losing form state.
export default class extends Controller {
  static values = { catchUpUrl: String };
  static targets = ["loadingTemplate", "messagesContainer"];

  connect() {
    const source = this.element.querySelector("turbo-cable-stream-source");
    if (!source) return;

    this.#initialConnectSeen = source.hasAttribute("connected");
    this.#disconnected = false;

    this.#observer = new MutationObserver(() => {
      const connected = source.hasAttribute("connected");

      if (!connected && this.#initialConnectSeen) {
        this.#disconnected = true;
        this.#showLoading();
      }

      if (connected && this.#disconnected) {
        this.#catchUp();
      }

      if (connected) this.#initialConnectSeen = true;
    });

    this.#observer.observe(source, {
      attributes: true,
      attributeFilter: ["connected"],
    });
  }

  disconnect() {
    this.#observer?.disconnect();
  }

  async #catchUp() {
    this.#disconnected = false;

    const lastTs = this.#maxMessageTimestamp() || "0";
    const url = this.catchUpUrlValue.replace("__ts__", lastTs);
    try {
      await get(url, { responseKind: "turbo-stream" });
    } finally {
      this.#removeLoading();
    }
  }

  #showLoading() {
    if (document.getElementById("massimo-reconnecting")) return;
    this.messagesContainerTarget.appendChild(this.loadingTemplateTarget.content.cloneNode(true));
  }

  #removeLoading() {
    document.getElementById("massimo-reconnecting")?.remove();
  }

  // Returns the highest updated_at microsecond timestamp found in the DOM,
  // so the catch-up fetch can request every message changed after that point
  // (covering both new messages and updates to existing ones).
  #maxMessageTimestamp() {
    const els = this.element.querySelectorAll("[data-updated-at]");
    if (els.length === 0) return null;
    let max = "0";
    for (const el of els) {
      if (el.dataset.updatedAt > max) max = el.dataset.updatedAt;
    }
    return max;
  }

  #observer;
  #initialConnectSeen;
  #disconnected;
}
